import { tt } from './i18n'
import { categoryColour, type CategoryColour } from './category-colour'

/**
 * §9.5's channel filter — *"kitchen + orders boards get channel filter tabs (each channel, multi-select,
 * with an all-channels tab)"*.
 *
 * It lives in `@agos/shared` because two modules need the same answer and §5 forbids one owning it for the
 * other: `/kitchen` and `/lists` are both `manager-orders`, but `/preorders`, `/dispatch` and the kasa
 * screens all label a channel too, and every one of them currently hard-codes the same three names.
 *
 * **This replaces `ORDER_TYPE_LABELS`**, whose own docblock said so:
 *
 * > The three channels legacy hardcodes. BACKEND.md §10A.1 replaced them with franchise-managed
 * > `sales_channels`, and §9.5 replaces this map with a channel lookup — after parity.
 *
 * And `SalesChannel`'s docblock says why it matters: *"Ask what a channel does, never what it is called.
 * `channel.usesDrivers()` keeps working when a franchise adds a fourth channel; `"delivery".equals(type)`
 * does not."* The same holds for the label — a hard-coded map cannot name a channel it has never heard of.
 *
 * **FB-174's other half.** The map labels `bar` *Osobný odber* while `sales_channels` names it **BAR**. Two
 * names for one channel, on screens a manager reads side by side. The server's name wins, because it is the
 * one the franchise chose and the one every §10A payload carries.
 */

export interface FilterChannel {
  slug: string
  name: string
  isActive: boolean
  sortOrder: number
  /**
   * The capabilities, for the screens that place an order rather than list one.
   *
   * `SalesChannel`: *"Ask what a channel **does**, never what it is called."* The order builder asked what
   * it was called — `orderType === 'restaurant'` for the table field, `=== 'delivery'` for the address —
   * so a fourth channel would have collected neither.
   */
  requiresDeliveryAddress: boolean
  usesTables: boolean
  usesDrivers: boolean
  onPremise: boolean
  allowsDeferredPayment: boolean
  /**
   * §10A.87 — paid and printed the moment it is taken, so it is never on a board.
   *
   * The owner's *Do ruky*. Every other capability here says what an order **needs**; this one says
   * when it is **finished**.
   */
  settlesImmediately: boolean
  /**
   * §9.38 — the channel's colour, or `null` when it has none or one this build does not know.
   *
   * Read through `categoryColour`, the same gate a category's colour passes, because the owner asked
   * for *farbičkovanie ako pri kategóriách* and one rule means an unrecognised value renders as no
   * colour on both screens rather than as a broken chip on one.
   */
  colour: CategoryColour | null
}

export function filterChannelsOf(payload: unknown): FilterChannel[] {
  if (!Array.isArray(payload)) return []
  return payload
    .flatMap((entry) => {
      const row = entry as Record<string, unknown>
      const slug = typeof row.slug === 'string' && row.slug !== '' ? row.slug : null
      if (slug === null) return []
      return [
        {
          slug,
          name: typeof row.name === 'string' && row.name !== '' ? row.name : slug,
          isActive: row.isActive === true,
          sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : 0,
          requiresDeliveryAddress: row.requiresDeliveryAddress === true,
          usesTables: row.usesTables === true,
          usesDrivers: row.usesDrivers === true,
          onPremise: row.onPremise === true,
          allowsDeferredPayment: row.allowsDeferredPayment === true,
          settlesImmediately: row.settlesImmediately === true,
          // The wire spells it `color`; the helpers it is used with are `categoryColour`.
          colour: categoryColour(row.color),
        },
      ]
    })
    .sort((left, right) => left.sortOrder - right.sortOrder || left.slug.localeCompare(right.slug))
}

/**
 * The channels to offer as filter tabs.
 *
 * Active ones, **plus any inactive channel the visible orders actually use** — switching a channel off
 * stops new orders choosing it, it does not remove the ones already placed, and a board that hid the tab
 * would leave those orders unfilterable and unexplained.
 */
export function offeredChannels(
  channels: readonly FilterChannel[],
  usedSlugs: readonly string[],
): FilterChannel[] {
  const used = new Set(usedSlugs)
  return channels.filter((channel) => channel.isActive || used.has(channel.slug))
}

/**
 * What to call a channel, from the server when it knows and from the slug when it does not.
 *
 * A slug with no channel behind it is shown as itself rather than blank: an order carrying a type the
 * channel table has never heard of is exactly the case worth seeing.
 */
export function channelName(slug: string, channels: readonly FilterChannel[]): string {
  const match = channels.find((channel) => channel.slug === slug)
  return match?.name ?? (slug === '' ? tt('bez kanála') : slug)
}

// `isKnownChannel` and `unknownSlugs` were here and are deleted — §9.35, 2026-08-06. They named the
// order types no channel explains, and no screen asked. `unknownSlugs` had no caller and
// `isKnownChannel` had exactly one, inside it, which is the chain that showed the sweep's own script
// was still missing wrapped signatures.
//
// Measured before deleting: every order type in the data — `delivery` 7 436, `restaurant` 2 266,
// `bar` 1 086 — has a channel row, so the list they built was empty.
//
// **The case they guarded is already handled where it shows.** `channelName` above answers an
// unexplained slug with the slug itself rather than a blank, so an order carrying a type the table
// has never heard of appears in the filter reading `vydaj` — visible, and in the one place somebody
// is looking. A second, silent list of the same slugs adds nothing to that.

// ─── The selection ───────────────────────────────────────────────────────────

/**
 * A multi-select where **empty means all**.
 *
 * Not a set containing every slug: a franchise that adds a channel while somebody has the board open
 * should have it included, and *everything* is a different intent from *these four, which happen to be all
 * of them today*.
 */
export type ChannelSelection = readonly string[]

export const ALL_CHANNELS: ChannelSelection = []

export function isAllSelected(selection: ChannelSelection): boolean {
  return selection.length === 0
}

export function isSelected(slug: string, selection: ChannelSelection): boolean {
  return isAllSelected(selection) || selection.includes(slug)
}

/**
 * Toggling one channel.
 *
 * From *all*, tapping one narrows to that one — which is what somebody wants when they tap a tab on a busy
 * board. Deselecting the last one goes back to *all* rather than to a board showing nothing, because an
 * empty board is indistinguishable from a quiet evening.
 */
export function toggleChannel(slug: string, selection: ChannelSelection): ChannelSelection {
  if (isAllSelected(selection)) return [slug]
  if (selection.includes(slug)) {
    const next = selection.filter((entry) => entry !== slug)
    return next.length === 0 ? ALL_CHANNELS : next
  }
  return [...selection, slug]
}

export function selectAll(): ChannelSelection {
  return ALL_CHANNELS
}

/** Whether an order belongs on the board given the current selection. */
export function matchesSelection(slug: string, selection: ChannelSelection): boolean {
  return isAllSelected(selection) || selection.includes(slug)
}

export function filterByChannel<T extends { type?: string | null }>(
  rows: readonly T[],
  selection: ChannelSelection,
): T[] {
  if (isAllSelected(selection)) return [...rows]
  return rows.filter((row) => matchesSelection(row.type ?? '', selection))
}

/**
 * What the board is showing, said so a narrowed board cannot be mistaken for a quiet one.
 *
 * The failure this prevents is specific: somebody taps *Rozvoz*, walks away, comes back to an empty board
 * and reads it as *no orders* when it means *no delivery orders*.
 */
export function selectionNote(
  selection: ChannelSelection,
  channels: readonly FilterChannel[],
  shown: number,
  total: number,
): string {
  if (isAllSelected(selection)) return ''
  const names = selection.map((slug) => channelName(slug, channels)).join(', ')
  if (shown === 0) {
    return `${tt('Filtrujete na')} ${names} - ${tt('a v tomto kanáli nič nie je. Celkovo je tu')} ${total}.`
  }
  return `${tt('Zobrazené len')} ${names} (${shown} ${tt('z')} ${total}).`
}

/** The label on the *all* tab, which is a tab like any other so a thumb can reach it. */
export function allLabel(): string {
  return tt('Všetky')
}

/**
 * A selection that survives a poll but not a session.
 *
 * The boards refetch every ten seconds and re-render; the filter must not reset under somebody's hand.
 * It is deliberately **not** persisted across sessions — a kitchen screen left filtered overnight is how
 * an order gets missed, and the next shift must open on everything.
 */
export function sanitiseSelection(
  selection: ChannelSelection,
  offered: readonly FilterChannel[],
): ChannelSelection {
  const known = new Set(offered.map((channel) => channel.slug))
  const kept = selection.filter((slug) => known.has(slug))
  return kept.length === selection.length ? selection : kept
}

// ─── What a channel needs before an order can be placed on it ────────────────

/**
 * The fields a new order on this channel must carry.
 *
 * Read off the **capabilities**, never the slug. The order builder asked
 * `orderType === 'restaurant'` for the table number and `=== 'delivery'` for the address and phone, so a
 * franchise adding a fourth channel would have been asked for neither — and `SalesChannel`'s own docblock
 * is explicit that this is the mistake to avoid.
 *
 * The phone follows the address: a delivery needs somebody to ring from the door, and every channel that
 * requires an address in this data also uses drivers.
 */
export interface ChannelRequirements {
  address: boolean
  phone: boolean
  table: boolean
}

export function requirementsFor(
  slug: string,
  channels: readonly FilterChannel[],
): ChannelRequirements {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) {
    // An unknown channel asks for nothing rather than guessing: the server validates, and inventing a
    // requirement would block an order the server would have accepted.
    return { address: false, phone: false, table: false }
  }
  return {
    address: channel.requiresDeliveryAddress,
    phone: channel.requiresDeliveryAddress,
    table: channel.usesTables,
  }
}

/**
 * Whether the till settles this channel on the spot — §10A.87, the owner's *Do ruky*.
 *
 * A separate question from `belongsOnRoad`, though the same flag answers both: one decides where an
 * order is looked after, this one decides what the person who took it does next.
 *
 * **An unknown channel does not settle itself.** Same reasoning as the board: a channel nobody can
 * resolve must leave its order somewhere it will be noticed.
 */
export function settlesAtOnce(slug: string, channels: readonly FilterChannel[]): boolean {
  return channels.find((entry) => entry.slug === slug)?.settlesImmediately === true
}

/** The channels a *new* order may be placed on. A switched-off channel is not one of them. */
export function orderableChannels(channels: readonly FilterChannel[]): FilterChannel[] {
  return channels.filter((channel) => channel.isActive)
}

// ─── Where an order belongs, for the dispatch board ──────────────────────────

/**
 * Whether an order on this channel belongs on the dispatch board.
 *
 * The board is the road. A channel that serves tables belongs to the restaurant screen — which is what
 * `type !== 'restaurant'` meant, and what it stopped meaning the moment a franchise added a second
 * table-serving channel. `na_sekeru` is exactly that channel, and it would have appeared on the road.
 */
export function belongsOnRoad(slug: string, channels: readonly FilterChannel[]): boolean {
  const channel = channels.find((entry) => entry.slug === slug)
  // A channel the table has never heard of stays on the board: an order nobody can see is worse than an
  // order on the wrong screen, and the dispatcher can tell at a glance.
  if (channel === undefined) return true
  // §10A.87 — a channel that settles at once is finished before the board could draw it. Checked
  // before `usesTables`, because such a channel has no tables either and would otherwise pass.
  if (channel.settlesImmediately) return false
  return !channel.usesTables
}

/**
 * Whether anybody drives an order on this channel - §9.309, the owner's item B.
 *
 * **The channel's own capability, not an inference from the other flags.** `sales-channels.ts` states
 * the rule this file lives by: *"Ask what a channel **does**, never what it is called."* The first
 * draft of §9.309 asked `!isCounterCollection` instead and would have been wrong on the owner's data:
 * `bar` has `usesDrivers: true` and **137 of its 1 082 orders have a driver** (FB-181 measured that),
 * so a counter channel here really is driven one time in eight.
 *
 * **An unresolvable channel counts as driven**, the same way `belongsOnRoad` keeps it on the board: a
 * dispatcher who cannot hand over an order they can see is worse off than one offered a picker that
 * turns out to be unnecessary.
 */
export function usesDrivers(slug: string, channels: readonly FilterChannel[]): boolean {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) return true
  return channel.usesDrivers
}

/**
 * Whether somebody is standing at the counter waiting for this one.
 *
 * On the premises but not at a table. That is what `type === 'bar'` meant, and the reason the board sorts
 * these first: a person at the counter is absorbing the wait themselves, where a delivery still has a
 * driver's journey to absorb it.
 */
export function isCounterCollection(slug: string, channels: readonly FilterChannel[]): boolean {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) return false
  return channel.onPremise && !channel.usesTables
}

/**
 * Whether an order on this channel may be put on a tab.
 *
 * `OrderTabService.append` refuses otherwise, and says why: *"Letting any order onto a tab would take it
 * out of the day's takings — the handover excludes open tabs — so the channel's flag is what separates
 * **not yet paid** from **somebody forgot to charge for this**."*
 */
export function allowsTab(slug: string, channels: readonly FilterChannel[]): boolean {
  return channels.find((entry) => entry.slug === slug)?.allowsDeferredPayment === true
}

/**
 * What to call a sales channel on a report — `FRONTEND.md` §9.94.
 *
 * Four screens carried a hand-written slug-to-label map, which §9.5's rule is the opposite of: *ask
 * what a channel does, never what it is called*. A rename never reached them, a slug outside the map
 * printed raw, and one map named `pickup` where this franchise's slug is `bar` — so its *Osobný
 * odber* never resolved at all.
 *
 * The franchise's own `name` wins. `fallbacks` covers a slug the API did not return — a report drawn
 * before the channels have loaded still reads properly — and the slug itself is the last resort,
 * because a row labelled `na_sekeru` is worse than a good label and far better than a dropped row.
 */
export function channelLabel(
  slug: string | null | undefined,
  channels: readonly FilterChannel[],
  fallbacks: Readonly<Record<string, string>> = {},
): string {
  const wanted = (slug ?? '').trim()
  if (wanted === '') return ''
  const named = channels.find((channel) => channel.slug === wanted)?.name?.trim()
  if (named !== undefined && named !== '') return named
  return fallbacks[wanted] ?? wanted
}
